import { Injectable, OnDestroy } from '@angular/core';
import { MatStepperService } from '../lead-task-services/mat-stepper.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LeadTaskService } from '../lead-task.service';
import { ErrorListService } from '../../../reusable-components/error-list/error-list.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotPossibleReason, PlaybackDeviceLocation } from '../../../examinationResult.model';

@Injectable()
export class GeneralInformationService implements OnDestroy {
  private readonly active = new BehaviorSubject<boolean>(false);
  public readonly active$ = this.active.asObservable();
  private readonly matStepperSubscription: Subscription;
  private readonly notAllRoomsExaminedError =
    'components.general-information.not-all-rooms-visited-error';
  private readonly noteNeededError =
    'components.general-information.note-needed-error';
  private readonly imageNeededError =
    'components.general-information.image-needed-error';
  private readonly playbackDeviceLocationNeededError =
    'components.general-information.playback-device-location-needed-error';
  private readonly guestCountNeededError =
    'components.general-information.guest-count-needed-error';
  private readonly notesLengthError =
    'components.general-information.notes-too-long-error';

  public readonly errorListService: ErrorListService;
  private errorListServiceNoteRequired: ErrorListService;
  private errorListServiceImageRequired: ErrorListService;
  private errorListServicePlaybackDeviceRequired: ErrorListService;
  private errorListServiceGuestCountRequired: ErrorListService;
  private errorListServiceNotesLength: ErrorListService;
  private completelyClosed: NotPossibleReason = undefined;

  constructor(
    private matStepperService: MatStepperService,
    private leadTaskService: LeadTaskService,
  ) {
    this.matStepperSubscription = this.matStepperService.currentStep$.subscribe(
      (index: number) => this.active.next(index === 2),
    );
    this.errorListService = leadTaskService.errorListServiceGeneralInformation;
    this.errorListServiceNoteRequired =
      this.errorListService.create('note-required');
    this.errorListServiceImageRequired =
      this.errorListService.create('image-required');
    this.errorListServicePlaybackDeviceRequired =
      this.errorListService.create('playback-device-required');
    this.errorListServiceGuestCountRequired =
      this.errorListService.create('guest-count-required');
    this.errorListServiceNotesLength =
      this.errorListService.create('notes-length');

    this.active$.pipe(takeUntilDestroyed()).subscribe((active) => {
      if (active) {
        const roomDraftMissing = !this.leadTaskService
          .getUsageSpaceIds()
          .concat(this.leadTaskService.getSelfCreatedRoomsIds())
          .map((id: string) => this.leadTaskService.hasRoomDraft(id))
          .every((hasRoomDraft: boolean) => hasRoomDraft);

        const inRoomExamination =
          leadTaskService.getCurrentRoomExaminationId() !== undefined;
        if (
          (roomDraftMissing || inRoomExamination) &&
          this.completelyClosed === undefined
        ) {
          this.errorListService.addError(this.notAllRoomsExaminedError);
        } else {
          this.errorListService.removeError(this.notAllRoomsExaminedError);
        }
        this.updateClosedErrors();
        this.updatePlaybackAndGuestErrors();
      }
    });
  }

  public setNotes(notes: string) {
    this.leadTaskService.setNotes(notes);
    this.updateClosedErrors();
    this.updateNotesLengthError(notes);
  }

  public addImage(image: string) {
    this.leadTaskService.addImage(image);
    this.updateClosedErrors();
  }

  public removeImageWithDescription(index: number) {
    this.leadTaskService.removeImageWithDescription(index);
    this.updateClosedErrors();
  }

  public setCompletelyClosed(completelyClosed: NotPossibleReason) {
    this.completelyClosed = completelyClosed;
    this.updateClosedErrors();
    if (completelyClosed) {
      this.errorListService.removeError(this.notAllRoomsExaminedError);
      this.leadTaskService.setPlaybackDevicesLocation(null);
      this.leadTaskService.setGuestsPresent(false);
      this.leadTaskService.setGuestCount(null);
    }
    this.updatePlaybackAndGuestErrors();
    this.matStepperService.disableBackToRooms(completelyClosed !== undefined);
  }

  public setPlaybackDevicesVisible(value: boolean): void {
    this.leadTaskService.setPlaybackDevicesVisible(value);
    this.updatePlaybackAndGuestErrors();
  }

  public setPlaybackDevicesLocation(value: PlaybackDeviceLocation | null): void {
    this.leadTaskService.setPlaybackDevicesLocation(value);
    this.updatePlaybackAndGuestErrors();
  }

  public setGuestsPresent(value: boolean): void {
    this.leadTaskService.setGuestsPresent(value);
    this.updatePlaybackAndGuestErrors();
  }

  public setGuestCount(value: number | null): void {
    this.leadTaskService.setGuestCount(value);
    this.updatePlaybackAndGuestErrors();
  }

  private updateClosedErrors() {
    if (
      this.completelyClosed !== undefined &&
      ['CLIENT_CONTACT', 'BANNED_FROM_PREMISES', 'CURRENTLY_CLOSED'].includes(
        this.completelyClosed,
      ) &&
      !this.leadTaskService.getNotes().length
    ) {
      this.errorListServiceNoteRequired.addError(this.noteNeededError);
    } else {
      this.errorListServiceNoteRequired.removeError(this.noteNeededError);
    }
    const noImages = !this.leadTaskService.getImages().length;
    if (
      (this.completelyClosed === undefined && noImages) ||
      (!!this.completelyClosed && ['VACANCY', 'NO_LONGER_EXISTS'].includes(this.completelyClosed) && noImages)
    ) {
      this.errorListServiceImageRequired.addError(this.imageNeededError);
    } else {
      this.errorListServiceImageRequired.removeError(this.imageNeededError);
    }
  }

  private updatePlaybackAndGuestErrors(): void {
    if (
      this.completelyClosed === undefined &&
      !this.leadTaskService.getPlaybackDevicesLocation()
    ) {
      this.errorListServicePlaybackDeviceRequired.addError(
        this.playbackDeviceLocationNeededError,
      );
    } else {
      this.errorListServicePlaybackDeviceRequired.removeError(
        this.playbackDeviceLocationNeededError,
      );
    }

    const guestCount = this.leadTaskService.getGuestCount();
    if (
      this.completelyClosed === undefined &&
      this.leadTaskService.getGuestsPresent() &&
      (guestCount === null || !Number.isInteger(guestCount) || guestCount < 1)
    ) {
      this.errorListServiceGuestCountRequired.addError(this.guestCountNeededError);
    } else {
      this.errorListServiceGuestCountRequired.removeError(this.guestCountNeededError);
    }
  }

  private updateNotesLengthError(notes: string): void {
    if (notes.length > 200) {
      this.errorListServiceNotesLength.addError(this.notesLengthError);
    } else {
      this.errorListServiceNotesLength.removeError(this.notesLengthError);
    }
  }

  ngOnDestroy(): void {
    this.matStepperSubscription.unsubscribe();
  }
}
